import React, { useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Dropdown } from 'primereact/dropdown';
import { MenuItem } from 'primereact/menuitem';
import { ComplaintSummaryItem, ComplaintQuickViewResponse, getComplaintQuickView } from '../../services/order/order.service';
import ComplaintQuickViewDialog from './ComplaintQuickViewDialog';
import ComplaintEditDialog from './ComplaintEditDialog';

interface ComplaintSummaryDataTableProps {
    data: ComplaintSummaryItem[];
    pageNo: number;
    pageSize: number;
    handlepagechange: (event: any) => void;
    totalRecordsCount: number;
    onRefresh?: () => void;
    sortField?: string;
    sortOrder?: 0 | 1 | -1 | null | undefined;
    onSort?: (event: any) => void;
    onSortChange?: (value: string) => void;
    totalCost?: number;
    costPercentage?: number;
}

const ComplaintSummaryDataTable: React.FC<ComplaintSummaryDataTableProps> = ({
    data,
    pageNo,
    pageSize,
    handlepagechange,
    totalRecordsCount,
    onRefresh,
    sortField,
    sortOrder,
    onSort,
    onSortChange,
    totalCost,
    costPercentage
}) => {

    const sortOptions = [
        { label: 'Critical', value: 'critical' },
        { label: 'Open > 7 Days', value: 'open7days' },
        { label: 'Invoice Value', value: 'invoicevalue' },
        { label: 'High Priority', value: 'highpriority' },
        { label: 'Low Priority', value: 'lowpriority' },
        { label: 'Newest', value: 'newest' },
        { label: 'Oldest', value: 'oldest' }
    ];

    const menuRefs = useRef<{ [key: number]: Menu | null }>({});
    const [quickViewVisible, setQuickViewVisible] = useState(false);
    const [editVisible, setEditVisible] = useState(false);
    const [selectedComplaintId, setSelectedComplaintId] = useState<number | null>(null);
    const [selectedComplaint, setSelectedComplaint] = useState<ComplaintQuickViewResponse | null>(null);

    // ================= DATE FORMAT =================
    const formatDate = (dateString: string) => {
        if (!dateString) return '-';

        // Treat as UTC if no timezone specified
        const dateStr = (dateString.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateString))
            ? dateString
            : `${dateString}Z`;

        return new Date(dateStr).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata'
        });
    };

    // ================= ID WITH ALERTS =================
    const complaintIdTemplate = (rowData: ComplaintSummaryItem) => {
        return (
            <div className="flex align-items-center">
                <span className="font-semibold">{rowData.complaintId}</span>
            </div>
        );
    };

    // ================= STATUS CHIP =================
    const statusTemplate = (rowData: ComplaintSummaryItem) => {
        if (!rowData.status) return '-';
        const status = rowData.status.toLowerCase();

        const map: any = {
            'new': { cls: 'status-new', icon: 'pi-circle-fill' },
            'in progress': { cls: 'status-progress', icon: 'pi-sync' },
            'inprogress': { cls: 'status-progress', icon: 'pi-sync' },
            'resolved': { cls: 'status-resolved', icon: 'pi-check-circle' },
            'closed': { cls: 'status-closed', icon: 'pi-lock' },
            'rejected': { cls: 'status-rejected', icon: 'pi-times-circle' }
        };

        const s = map[status] || map['new'];

        return (
            <span className={`badge-chip ${s.cls}`}>
                <i className={`pi ${s.icon}`} />
                {rowData.status}
            </span>
        );
    };

    // ================= PRIORITY CHIP =================
    const priorityTemplate = (rowData: ComplaintSummaryItem) => {
        if (!rowData.priority) return '-';
        const p = rowData.priority.toUpperCase();

        const map: any = {
            'HIGH': { cls: 'priority-high', icon: 'pi-exclamation-triangle' },
            'MEDIUM': { cls: 'priority-medium', icon: 'pi-exclamation-circle' },
            'LOW': { cls: 'priority-low', icon: 'pi-info-circle' }
        };

        const pr = map[p] || map['LOW'];

        return (
            <span className={`badge-chip ${pr.cls}`}>
                <i className={`pi ${pr.icon}`} />
                {p}
            </span>
        );
    };

    // ================= TYPE CHIP =================
    const complaintTypeTemplate = (rowData: ComplaintSummaryItem) => {
        if (!rowData.complaintType) return '-';
        const t = rowData.complaintType.toLowerCase();

        const map: any = {
            'product': { cls: 'type-product', icon: 'pi-box' },
            'service': { cls: 'type-service', icon: 'pi-wrench' },
            'internal': { cls: 'type-internal', icon: 'pi-building' }
        };

        const tp = map[t] || map['product'];

        return (
            <span className={`badge-chip ${tp.cls}`}>
                <i className={`pi ${tp.icon}`} />
                {rowData.complaintType}
            </span>
        );
    };

    // ================= COST =================
    const costTemplate = (rowData: ComplaintSummaryItem) => (
        <span className="font-semibold">
            {rowData.costPerQtyInRs != null ? `₹ ${rowData.costPerQtyInRs.toLocaleString('en-IN')}` : '-'}
        </span>
    );

    // ================= CUSTOMER & PHONE =================
    const customerTemplate = (rowData: ComplaintSummaryItem) => rowData.customer || '-';
    const phoneTemplate = (rowData: ComplaintSummaryItem) => rowData.phonenumber || '-';

    // ================= CREATED / MODIFIED =================
    const createdInfoTemplate = (rowData: ComplaintSummaryItem) => (
        <div>
            <div className="font-semibold">{rowData.createdBy || '-'}</div>
            <div className="text-500 text-sm">{formatDate(rowData.createdOn)}</div>
        </div>
    );

    const modifiedInfoTemplate = (rowData: ComplaintSummaryItem) => {
        if (!rowData.lastModifiedOn) {
            return <div>-</div>;
        }
        
        return (
            <div>
                <div className="font-semibold">{rowData.lastModifiedBy || '-'}</div>
                <div className="text-500 text-sm">{formatDate(rowData.lastModifiedOn)}</div>
            </div>
        );
    };

    // ================= INVOICE VALUE TEMPLATE =================
    const invoiceValueTemplate = (rowData: ComplaintSummaryItem) => {
        const val = rowData.invoiceValue;
        const isHigh = rowData.invoiceGreaterValue; // or check > 100 directly if preferred

        if (val == null) return '-';

        return (
            <span style={{
                color: isHigh ? '#00838f' : 'inherit',
                fontWeight: isHigh ? 'bold' : 'normal',
                background: isHigh ? 'rgba(0, 131, 143, 0.1)' : 'transparent',
                padding: '2px 6px',
                borderRadius: '4px'
            }}>
                ₹ {val.toLocaleString('en-IN')}
            </span>
        );
    };

    // ================= DAYS OPEN TEMPLATE =================
    const daysOpenTemplate = (rowData: ComplaintSummaryItem) => {
        const days = rowData.daysOpen;
        const isOld = rowData.moreThanOneWeek; // or check > 7 directly if preferred

        if (days == null) return '-';

        return (
            <span style={{
                color: isOld ? '#d32f2f' : 'inherit',
                fontWeight: isOld ? 'bold' : 'normal',
                background: isOld ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                padding: '2px 6px',
                borderRadius: '4px'
            }}>
                {days} days
            </span>
        );
    };

    // ================= ACTION MENU =================
    const actionsTemplate = (rowData: ComplaintSummaryItem) => {
        const menuItems: MenuItem[] = [
            { label: 'Quick View', icon: 'pi pi-eye', command: () => handleQuickView(rowData) },
            { label: 'Edit', icon: 'pi pi-pencil', command: () => handleEdit(rowData) }
        ];

        return (
            <>
                <Menu model={menuItems} popup ref={(el) => menuRefs.current[rowData.complaintId] = el} />
                <Button icon="pi pi-ellipsis-v" text rounded onClick={(e) => menuRefs.current[rowData.complaintId]?.toggle(e)} />
            </>
        );
    };

    // ================= QUICK VIEW =================
    const handleQuickView = (c: ComplaintSummaryItem) => {
        setSelectedComplaintId(c.complaintId);
        setQuickViewVisible(true);
    };

    // ================= EDIT =================
    const handleEdit = async (c: ComplaintSummaryItem) => {
        const fullComplaint = await getComplaintQuickView(c.complaintId);
        setSelectedComplaint(fullComplaint);
        setEditVisible(true);
    };

    const handleEditSuccess = () => onRefresh?.();

    // ================= ROW HIGHLIGHTING =================
    const rowClassName = (rowData: ComplaintSummaryItem) => {
        const hasWeekAlert = rowData.moreThanOneWeek;
        const hasInvoiceAlert = rowData.invoiceGreaterValue;

        if (hasWeekAlert && hasInvoiceAlert) {
            return 'alert-row-both';
        } else if (hasInvoiceAlert) {
            return 'alert-row-high-invoice';
        } else if (hasWeekAlert) {
            return 'alert-row-week-delay';
        }
        return '';
    };

    return (
        <>
            {/* ================= BADGE CSS ================= */}
            <style>{`
                /* Table Card Wrapper */
                .table-card-wrapper {
                    background: #ffffff;
                    border-radius: 16px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                    border: 1px solid rgba(76, 175, 80, 0.15);
                    overflow: hidden;
                }

                .table-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1.25rem 1.5rem;
                    border-bottom: 1px solid #f0f0f0;
                    background: linear-gradient(135deg, #fafafa 0%, #ffffff 100%);
                }

                .table-header-left {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .table-icon {
                    width: 36px;
                    height: 36px;
                    background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .table-icon i {
                    color: white;
                    font-size: 1rem;
                }

                .table-title {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1f2937;
                }

                .refresh-btn {
                    background: transparent !important;
                    border: 1px solid #e5e7eb !important;
                    color: #6b7280 !important;
                    border-radius: 8px !important;
                    padding: 0.5rem 1rem !important;
                    transition: all 0.2s ease !important;
                }

                .refresh-btn:hover {
                    border-color: #4CAF50 !important;
                    color: #4CAF50 !important;
                    background: rgba(76, 175, 80, 0.05) !important;
                }

                .badge-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 12px;
                    border-radius: 999px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .badge-chip i {
                    font-size: 0.7rem;
                }

                /* STATUS */
                .status-new { background:#E3F2FD; color:#1E88E5; }
                .status-progress { background:#FFF3E0; color:#FB8C00; }
                .status-resolved { background:#E8F5E9; color:#43A047; }
                .status-closed { background:#F5F5F5; color:#757575; }
                .status-rejected { background:#FFEBEE; color:#E53935; }

                /* PRIORITY */
                .priority-high { background:#FFEBEE; color:#DC2626; }
                .priority-medium { background:#FFF3E0; color:#EA580C; }
                .priority-low { background:#E8F5E9; color:#059669; }

                /* TYPE */
                .type-product { background:#E0F2FE; color:#1E40AF; }
                .type-service { background:#FCE7F3; color:#BE185D; }
                .type-internal { background:#ECFEFF; color:#0369A1; }

                /* PAGINATOR INLINE */
                .p-datatable .p-paginator {
                    justify-content: center;
                    gap: 0.5rem;
                }

                .p-datatable .p-paginator .p-dropdown {
                    width: 4rem !important;
                    min-width: 0 !important;
                    height: 2.5rem;
                    align-items: center;
                }

                .p-datatable .p-paginator .p-dropdown .p-dropdown-label {
                    padding-right: 0 !important;
                    padding-left: 0.5rem !important;
                    font-size: 0.875rem;
                }

                /* HEADERS */
                .p-datatable .p-datatable-thead > tr > th {
                    color: #10492a;
                    font-weight: 600;
                    background: #fafafa;
                }

                /* ROW HOVER - DISABLED */
                .p-datatable .p-datatable-tbody > tr:hover {
                    background: #ffffff !important;
                }

                /* MENU HOVER */
                .p-menu .p-menuitem-link:hover {
                    background-color: #4CAF50 !important;
                    color: white !important;
                }

                .p-menu .p-menuitem-link:hover .p-menuitem-icon,
                .p-menu .p-menuitem-link:hover .p-menuitem-text {
                    color: white !important;
                }

                .p-menu .p-menuitem-link {
                    transition: all 0.2s ease;
                }

                /* ALERT ICONS */
                .alert-icon {
                    font-size: 1.1rem;
                    padding: 0.4rem;
                    border-radius: 50%;
                    transition: all 0.2s ease;
                    cursor: help;
                }

                .invoice-alert {
                    color: #00838f;
                   
                }

                .invoice-alert:hover {
                    color: #006064;
                    transform: scale(1.1);
                }

                .week-alert {
                    color: #DC2626;
                }

                .week-alert:hover {
                  color: #DC2626;

                    transform: scale(1.1);
                }

                /* ROW HIGHLIGHTING FOR ALERTS */
                .p-datatable .p-datatable-tbody > tr.alert-row-high-invoice {
                    background: linear-gradient(90deg, rgba(0, 131, 143, 0.08) 0%, rgba(0, 131, 143, 0.02) 100%) !important;
                    border-left: 3px solid #00838f !important;
                }

                .p-datatable .p-datatable-tbody > tr.alert-row-week-delay {
                    background: linear-gradient(90deg, rgba(220, 38, 38, 0.08) 0%, rgba(220, 38, 38, 0.02) 100%) !important;
                    border-left: 3px solid #DC2626 !important;
                }

                .p-datatable .p-datatable-tbody > tr.alert-row-both {
                    background: linear-gradient(90deg, rgba(0, 131, 143, 0.12) 0%, rgba(220, 38, 38, 0.04) 100%) !important;
                    border-left: 3px solid #00838f !important;
                }

                /* Hover effect for alert rows - DISABLED (Keep original bg) */
                .p-datatable .p-datatable-tbody > tr.alert-row-high-invoice:hover {
                    background: linear-gradient(90deg, rgba(0, 131, 143, 0.08) 0%, rgba(0, 131, 143, 0.02) 100%) !important;
                }
                .p-datatable .p-datatable-tbody > tr.alert-row-week-delay:hover {
                      background: linear-gradient(90deg, rgba(220, 38, 38, 0.08) 0%, rgba(220, 38, 38, 0.02) 100%) !important;
                }
                .p-datatable .p-datatable-tbody > tr.alert-row-both:hover {
                     background: linear-gradient(90deg, rgba(0, 131, 143, 0.12) 0%, rgba(220, 38, 38, 0.04) 100%) !important;
                }

                /* DROPDOWN STYLING - MATCHING SEARCH COMPONENT */
                .complaint-search-dropdown-panel.p-dropdown-panel {
                    border-radius: 12px !important;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
                    border: 1px solid #f0f0f0 !important;
                }

                .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item {
                    border-radius: 8px !important;
                    margin: 2px 8px !important;
                    padding: 0.4rem 0.75rem !important;
                    font-size: 0.8125rem !important;
                }

                .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
                    background: #4CAF50 !important;
                    color: #ffffff !important;
                }

                .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
                    background: #388E3C !important;
                    color: #ffffff !important;
                }

                /* SORT TRIGGER */
                .sort-dropdown-custom {
                    border-radius: 8px !important;
                    border-color: #e0e0e0 !important;
                }

                .sort-dropdown-custom:hover {
                    border-color: #4CAF50 !important;
                }

                .sort-dropdown-custom.p-focus {
                    border-color: #4CAF50 !important;
                    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1) !important;
                }

                /* Dialog Styling - Imported from MyComplaints for "Good Design" */
                .complaint-dialog .p-dialog-header {
                    background: linear-gradient(to right, #f8f9fa, #ffffff);
                    border-bottom: 1px solid #f0f0f0;
                    padding: 1.5rem;
                    border-top-left-radius: 12px;
                    border-top-right-radius: 12px;
                }
                .complaint-dialog .p-dialog-content {
                    background: #ffffff;
                    padding: 0;
                }
                .complaint-dialog .p-dialog-footer {
                    border-top: 1px solid #f0f0f0;
                    background: #f8f9fa;
                    padding: 1rem 1.5rem;
                    border-bottom-left-radius: 12px;
                    border-bottom-right-radius: 12px;
                }
                .complaint-dialog .dialog-section {
                    background: #f8f9fa;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 1rem;
                    border: 1px solid #f0f0f0;
                }
                .complaint-dialog .dialog-section-title {
                    color: #4CAF50;
                    font-size: 0.75rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 0.75rem;
                }
                /* Mobile responsiveness for table header */
                @media screen and (max-width: 768px) {
                    .table-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 1rem;
                    }
                    
                    .table-header-left {
                        width: 100%;
                        flex-wrap: wrap;
                    }
                    
                    .table-title {
                        margin-right: auto;
                    }
                    
                    .table-header > div:last-child {
                        width: 100%;
                        margin-left: 0 !important;
                        margin-top: 0.5rem;
                    }
                    
                    .sort-dropdown-custom {
                        width: 100% !important;
                    }
                }
            `}</style>

            <div className="table-card-wrapper">
                {/* Table Header */}
                <div className="table-header">
                    <div className="table-header-left">
                        <div className="table-icon">
                            <i className="pi pi-list"></i>
                        </div>
                        <span className="table-title">Complaint Records</span>

                        {totalCost !== undefined && costPercentage !== undefined && (
                            <div className="ml-3 flex align-items-center bg-green-50 px-3 py-1 border-round-2xl border-1 border-green-200">
                                <span className="font-bold text-green-700"> Total COPQ:
                                    ₹ {totalCost.toLocaleString('en-IN')}
                                </span>
                                <span className="text-green-600 text-sm ml-1">
                                    ({costPercentage}%)
                                </span>
                            </div>
                        )}
                    </div>
                    {onSortChange && (
                        <div style={{ marginLeft: 'auto' }}>
                            <p style={{ marginBottom: '0.25rem', fontSize: '0.875rem', color: '#6B7280' }}> Sort By</p>
                            <Dropdown
                                value={sortField}
                                options={sortOptions}
                                onChange={(e) => onSortChange(e.value)}
                                placeholder="Sort By"
                                className="p-inputtext-sm sort-dropdown-custom"
                                panelClassName="complaint-search-dropdown-panel"
                                style={{ width: '200px' }}
                            />
                        </div>
                    )}
                </div>

                {/* DataTable */}
                <DataTable
                    value={data}
                    paginator
                    rows={pageSize}
                    first={(pageNo - 1) * pageSize}
                    totalRecords={totalRecordsCount}
                    onPage={handlepagechange}
                    lazy
                    scrollable
                    tableStyle={{ minWidth: '60rem' }}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    rowsPerPageOptions={[5, 10, 25, 50]}

                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"

                    rowClassName={rowClassName}
                    className="p-datatable-sm custom-footer"
                >
                    <Column field="complaintId" header="ID" body={complaintIdTemplate} sortable />
                    <Column field="customer" header="Customer" body={customerTemplate} />
                    <Column field="phonenumber" header="Phone" body={phoneTemplate} />
                    <Column field="invoiceValue" header="Invoice Value" body={invoiceValueTemplate} />
                    <Column field="daysOpen" header="Days Open" body={daysOpenTemplate} />
                    <Column field="complaintType" header="Type" body={complaintTypeTemplate} />
                    <Column field="status" header="Status" body={statusTemplate} style={{ minWidth: '120px' }} />
                    <Column field="priority" header="Priority" body={priorityTemplate} />
                    <Column field="costPerQtyInRs" header="COPQ" body={costTemplate} style={{ minWidth: '78px' }} />
                    <Column field="createdOn" header="Created" body={createdInfoTemplate} />
                    <Column field="lastModifiedOn" header="Modified" body={modifiedInfoTemplate} />
                    <Column header="" body={actionsTemplate} style={{ width: '60px' }} />
                </DataTable>
            </div>

            {/* Quick View */}
            <ComplaintQuickViewDialog
                visible={quickViewVisible}
                complaintId={selectedComplaintId}
                onHide={() => setQuickViewVisible(false)}
                showInternalFields={true}
            />

            {/* Edit */}
            <ComplaintEditDialog
                visible={editVisible}
                complaint={selectedComplaint}
                onHide={() => setEditVisible(false)}
                onSuccess={handleEditSuccess}
                showInternalFields={true}
            />
        </>
    );
};

export default ComplaintSummaryDataTable;
